'use client'

import { useEffect, useMemo, useState } from 'react'
import { useRouter } from 'next/navigation'
import Link from 'next/link'
import { format, parseISO } from 'date-fns'
import EventCard from '@/components/timed-ui/events/EventCard'
import SeriesCard from '@/components/timed-ui/events/SeriesCard'
import EventListItem from '@/components/timed-ui/events/EventListItem'
import SeriesListItem from '@/components/timed-ui/events/SeriesListItem'
import EventCardSkeleton from '@/components/timed-ui/common/EventCardSkeleton'
import { adaptEventsListForUI, type TimedUIEvent } from '@/lib/timed-ui/adapters'
import { citySlug, cityFromSlug } from '@/lib/timed-ui/city-slug'
import { groupEventsBySeries } from '@/lib/timed-ui/event-helpers'
import { useTheme } from '@/components/public/branding/ThemeProvider'
import { getTemplateConfig } from '@/lib/templates/template-config'
import { useFormatPrice } from '@/lib/timed-ui/use-format-price'

interface LocationContentDTO {
  cityName: string
  tagline: string | null
  blurb: string | null
  heroImage: string | null
  heroAlt: string | null
  displayMode: string | null
}

interface Props {
  slug: string
  content: LocationContentDTO | null
}

type DisplayMode = 'auto' | 'cards' | 'list' | 'showtimes'

/**
 * Public city landing page body. Mirrors the storefront home page's
 * event grouping + card rendering so /locations/[city] looks like the
 * rest of the site. Display mode can be overridden per-city by the
 * admin (auto/cards/list/showtimes).
 */
export default function LocationPageClient({ slug, content }: Props) {
  const router = useRouter()
  const { branding } = useTheme()
  const template = getTemplateConfig(branding?.layout)
  const seatedSeriesCalendar = (branding?.layout as Record<string, unknown>)?.seatedSeriesCalendar === true

  const mode: DisplayMode = (content?.displayMode as DisplayMode) || 'auto'

  const [events, setEvents] = useState<TimedUIEvent[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    fetch('/api/events/public')
      .then(r => r.json())
      .then(d => {
        if (d.success && d.events) setEvents(adaptEventsListForUI(d.events))
      })
      .finally(() => setLoading(false))
  }, [])

  const cityEvents = useMemo(() => {
    return events.filter(e => {
      const raw = e.venueCity || e.venue?.city || ''
      const cityOnly = raw.split(',')[0].trim()
      return citySlug(cityOnly) === slug
    })
  }, [events, slug])

  // Auto and List modes use series-grouping; "All cards" skips it.
  // The home page does the same cast — TimedUIEvent is a superset of
  // the EventBase shape that groupEventsBySeries expects.
  const grouped = useMemo(() => {
    if (mode === 'cards') return { series: [] as TimedUIEvent[], singles: cityEvents }
    const result = groupEventsBySeries(cityEvents as never) as unknown as { series: TimedUIEvent[]; singles: TimedUIEvent[] }
    return result
  }, [cityEvents, mode])

  const displayCity = content?.cityName
    || (cityEvents.length > 0
      ? (cityEvents[0].venueCity?.split(',')[0].trim() || cityEvents[0].venue?.city || cityFromSlug(slug))
      : cityFromSlug(slug))

  const venueNames = useMemo(() => {
    const names = new Set<string>()
    for (const e of cityEvents) {
      if (e.venue?.name) names.add(e.venue.name)
    }
    return Array.from(names)
  }, [cityEvents])

  // Same grid math the storefront home uses, so card density matches.
  const gridClasses =
    template.homepage.gridColumns.xl === 1 ? 'space-y-2' :
    template.homepage.gridColumns.xl === 2 ? 'grid grid-cols-1 lg:grid-cols-2 gap-8' :
    'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5'

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Breadcrumb */}
        <nav className="text-sm text-text-tertiary mb-4 flex items-center gap-1">
          <Link href="/" className="hover:text-text-secondary transition-colors">All events</Link>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <span className="text-text-secondary">{displayCity}</span>
        </nav>

        {/* Custom hero image (when configured) */}
        {content?.heroImage && (
          <div className="mb-6 sm:mb-8 rounded-2xl overflow-hidden border border-surface-border">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={content.heroImage}
              alt={content.heroAlt || displayCity}
              className="w-full h-48 sm:h-64 md:h-80 object-cover"
            />
          </div>
        )}

        {/* Hero text */}
        <header className="mb-8 sm:mb-10">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
              <svg className="w-6 h-6 sm:w-7 sm:h-7 text-[rgb(var(--color-primary))]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-3xl sm:text-4xl font-bold text-text-primary leading-tight">{displayCity}</h1>
              {content?.tagline && (
                <p className="text-base sm:text-lg text-text-secondary mt-1 leading-snug">{content.tagline}</p>
              )}
              <p className="text-sm text-text-tertiary mt-1">
                {loading
                  ? 'Loading events…'
                  : cityEvents.length === 0
                    ? 'No upcoming events scheduled here yet.'
                    : `${cityEvents.length} upcoming ${cityEvents.length === 1 ? 'event' : 'events'}${venueNames.length > 0 ? ` · ${venueNames.slice(0, 3).join(', ')}${venueNames.length > 3 ? '…' : ''}` : ''}`}
              </p>
            </div>
          </div>
          {content?.blurb && (
            <p className="text-sm sm:text-base text-text-secondary mt-4 sm:mt-5 max-w-3xl whitespace-pre-line">
              {content.blurb}
            </p>
          )}
        </header>

        {/* Content — display modes */}
        {loading ? (
          <EventCardSkeleton count={6} />
        ) : cityEvents.length === 0 ? (
          <EmptyState displayCity={displayCity} onAllEvents={() => router.push('/')} />
        ) : mode === 'showtimes' ? (
          <ShowtimesView events={cityEvents} />
        ) : mode === 'list' ? (
          <div className="space-y-2">
            {grouped.series.map(series => (
              <SeriesListItem key={series.id} series={series as never} seatedSeriesCalendar={seatedSeriesCalendar} />
            ))}
            {grouped.singles.map(event => (
              <EventListItem key={event.id} event={event} />
            ))}
          </div>
        ) : (
          /* mode === 'auto' or 'cards' — same renderer; 'cards' just
             didn't series-collapse upstream. */
          <div className={gridClasses}>
            {grouped.series.map(series => (
              <SeriesCard key={series.id} series={series as never} seatedSeriesCalendar={seatedSeriesCalendar} />
            ))}
            {grouped.singles.map(event => (
              <EventCard key={event.id} event={event} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function EmptyState({ displayCity, onAllEvents }: { displayCity: string; onAllEvents: () => void }) {
  return (
    <div className="bg-surface border border-surface-border rounded-2xl p-10 sm:p-14 text-center">
      <div className="w-14 h-14 rounded-full bg-surface-secondary mx-auto mb-4 flex items-center justify-center">
        <svg className="w-7 h-7 text-text-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      </div>
      <h2 className="text-lg font-semibold text-text-primary mb-1">Coming soon to {displayCity}</h2>
      <p className="text-sm text-text-secondary max-w-md mx-auto mb-5">
        We don&apos;t have any events here yet. Check back soon, or browse what&apos;s on elsewhere.
      </p>
      <button
        onClick={onAllEvents}
        className="inline-flex items-center gap-2 px-4 py-2 bg-[rgb(var(--color-primary))] text-white text-sm font-medium rounded-lg hover:opacity-90 transition-opacity"
      >
        See all events
      </button>
    </div>
  )
}

/**
 * Showtimes view — a single header (using the first event's hero +
 * description) followed by a compact date/time table. Ideal for a
 * residency where the same show happens many nights.
 */
function ShowtimesView({ events }: { events: TimedUIEvent[] }) {
  const formatPrice = useFormatPrice()
  // Sort chronologically.
  const sorted = useMemo(() => {
    return [...events].sort((a, b) => {
      const da = a.startDate ? new Date(a.startDate).getTime() : 0
      const db = b.startDate ? new Date(b.startDate).getTime() : 0
      return da - db
    })
  }, [events])

  // Use the first event as the "header" representative — its hero,
  // title, description.
  const hero = sorted[0]
  const hasMultipleTitles = new Set(sorted.map(e => e.title || e.name)).size > 1

  // Group by date for nicer scanning ("Friday, May 16 — 4 shows").
  const byDate = useMemo(() => {
    const map = new Map<string, TimedUIEvent[]>()
    for (const e of sorted) {
      const key = e.startDate ? format(parseISO(e.startDate), 'yyyy-MM-dd') : 'no-date'
      if (!map.has(key)) map.set(key, [])
      map.get(key)!.push(e)
    }
    return Array.from(map.entries())
  }, [sorted])

  return (
    <div className="space-y-6">
      {/* Header card */}
      <div className="bg-surface border border-surface-border rounded-2xl overflow-hidden">
        {hero.bannerUrl && (
          /* eslint-disable-next-line @next/next/no-img-element */
          <img src={hero.bannerUrl} alt={hero.title || hero.name} className="w-full h-48 sm:h-64 object-cover" />
        )}
        <div className="p-5 sm:p-6">
          <h2 className="text-xl sm:text-2xl font-semibold text-text-primary">
            {hasMultipleTitles ? 'Upcoming shows' : (hero.title || hero.name)}
          </h2>
          {!hasMultipleTitles && hero.description && (
            <p className="text-sm text-text-secondary mt-2 line-clamp-3">{hero.description}</p>
          )}
        </div>
      </div>

      {/* Date-grouped showtime rows */}
      <div className="bg-surface border border-surface-border rounded-2xl divide-y divide-surface-border overflow-hidden">
        {byDate.map(([key, dayEvents]) => {
          const d = key !== 'no-date' ? parseISO(key) : null
          return (
            <div key={key}>
              <div className="flex items-center gap-2 px-5 py-2.5 bg-surface-secondary/50">
                <svg className="w-4 h-4 text-text-tertiary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="text-sm font-semibold text-text-primary">
                  {d ? format(d, 'EEEE') : ''}
                </span>
                <span className="text-sm text-text-secondary">
                  {d ? format(d, 'MMM d, yyyy') : 'Date TBD'}
                </span>
                <span className="text-xs text-text-tertiary ml-auto">
                  {dayEvents.length} {dayEvents.length === 1 ? 'show' : 'shows'}
                </span>
              </div>
              {dayEvents.map(event => {
                const href = `/events/${event.slug || event.id}`
                const isSoldOut = event.status === 'SOLD_OUT'
                const timeStr = event.startDate ? format(parseISO(event.startDate), 'h:mm a') : ''
                const minPrice = event.ticketTypes.length > 0
                  ? Math.min(...event.ticketTypes.filter(t => t.allInPrice > 0 && !t.isAddon).map(t => t.allInPrice))
                  : 0
                return (
                  <Link
                    key={event.id}
                    href={href}
                    className={`flex items-center gap-3 px-5 py-3 hover:bg-surface-secondary transition-colors group ${isSoldOut ? 'opacity-60' : ''}`}
                  >
                    <span className={`inline-flex items-center justify-center w-[88px] flex-shrink-0 px-3 py-1.5 rounded-lg text-sm font-semibold ${isSoldOut ? 'bg-surface-secondary text-text-tertiary' : 'bg-primary/10 text-[rgb(var(--color-primary))]'}`}>
                      {timeStr || '--:--'}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-text-primary truncate">{event.title || event.name}</p>
                      {event.venue?.name && (
                        <p className="text-xs text-text-tertiary truncate">{event.venue.name}</p>
                      )}
                    </div>
                    <div className="flex-shrink-0 text-right">
                      {isSoldOut ? (
                        <span className="text-xs font-semibold text-error">Sold Out</span>
                      ) : minPrice > 0 ? (
                        <span className="text-sm font-semibold text-text-primary">{formatPrice(minPrice)}</span>
                      ) : null}
                    </div>
                    <svg className="w-4 h-4 text-text-tertiary flex-shrink-0 group-hover:text-text-secondary transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                )
              })}
            </div>
          )
        })}
      </div>
    </div>
  )
}
